import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import DatePicker from 'react-date-picker';
import { TextField } from '@mui/material';
import moment from 'moment';
import * as styles from './modal.styles';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import OfficeNotification from '../models/office-notification';
import Row from '../../../core/components/row/row.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import StyleConstants from '../../../core/styles.constants';
import NotificationFormType from '../enums/office-notification-form-types';
import RecurringDays from '../recurring.component';
import TimeInput from '../../../core/components/time-input/time-input';
import { maximumCharacters } from '../models/office.constants';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly handleCreate: (notification: OfficeNotification) => void;
  readonly isNotificationValid: (notification: OfficeNotification) => boolean;
  readonly officeId: string;
}

const CreateNotificationModal = ({ showModal, handleClose, handleCreate, isNotificationValid, officeId }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const initialNotification = new OfficeNotification();
  initialNotification.officeId = officeId;
  const [notification, setNotification] = useState<OfficeNotification>(initialNotification);
  const { t } = useTranslation();

  const resetModalState = (): void => {
    const newNotification = new OfficeNotification();
    newNotification.officeId = officeId;
    setNotification(newNotification);
  };

  const handleCloseModal = (): void => {
    resetModalState();
    handleClose();
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!showModal) {
      setIsLoading(false);
    }
  }, [showModal]);

  const handleSave = (): void => {
    const notificationCopy = { ...notification };
    if (isNotificationValid(notificationCopy)) {
      setIsLoading(true);
      resetModalState();
      handleCreate(notificationCopy);
      setIsLoading(false);
    }
  };

  const handleNotificationChange = (formType: NotificationFormType, value: unknown): void => {
    const notificationCopy = { ...notification };
    switch (formType) {
      case NotificationFormType.textDutch:
        notificationCopy.textDutch = value as string;
        break;
      case NotificationFormType.textEnglish:
        notificationCopy.textEnglish = value as string;
        break;
      case NotificationFormType.scheduledDateTime:
        if (Array.isArray(value)) {
          const [hour, minute] = value;
          notificationCopy.scheduledDateTime = moment(notification.scheduledDateTime)
            .set({ hour, minute, second: 0, millisecond: 0 })
            .toDate();
        } else {
          notificationCopy.scheduledDateTime = moment(value as Date)
            .set({
              hour: moment(notification.scheduledDateTime).hours(),
              minute: moment(notification.scheduledDateTime).minutes(),
              second: 0,
              millisecond: 0,
            })
            .toDate();
        }
        break;
      case NotificationFormType.expirationDate:
        if (value == null) {
          notificationCopy.expirationDate = undefined;
        } else {
          notificationCopy.expirationDate = moment(value as Date).endOf('day').toDate();
        }
        break;
      case NotificationFormType.isRecurring:
        notificationCopy.isRecurring = value as boolean;
        break;
      case NotificationFormType.recurringDays:
        notificationCopy.recurringDays = value as number[];
        break;
      default:
        break;
    }
    setNotification(notificationCopy);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCloseModal} style={styles.modalStyle(isMobile, 800, 'visible')}>
      <Column className={style(styles.modalBody(isMobile))} itemSpacing={28} mainAxisAlignment={MainAxisAlignment.Stretch}>
        <Row>
          <span className={style(styles.columnHeader(isMobile, 24))}>{t('create-office-notification')}</span>
        </Row>

        <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
          <TextField
            label={t('office-notification-text-dutch').toString()}
            multiline
            maxRows={2}
            value={notification.textDutch}
            inputProps={{ maxLength: maximumCharacters }}
            onChange={(event): void => {
              handleNotificationChange(NotificationFormType.textDutch, event.target.value.toString());
            }}
          />
        </Row>

        <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
          <TextField
            label={t('office-notification-text-english').toString()}
            multiline
            maxRows={2}
            value={notification.textEnglish}
            inputProps={{ maxLength: maximumCharacters }}
            onChange={(event): void => {
              handleNotificationChange(NotificationFormType.textEnglish, event.target.value.toString());
            }}
          />
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
          <span className={style(styles.modalText)}>{t('choose-scheduled-date-time')}</span>
          <DatePicker
            clearIcon={null}
            allowPartialRange
            calendarClassName={style(styles.calendar)}
            format="dd/MM/yyyy"
            value={notification.scheduledDateTime}
            minDate={new Date()}
            onChange={(value): void => {
              handleNotificationChange(NotificationFormType.scheduledDateTime, value);
            }}
          />
          <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
            <TimeInput
              hour={moment(notification.scheduledDateTime).hours()}
              minute={moment(notification.scheduledDateTime).minutes()}
              onChange={(hour, minute): void => {
                handleNotificationChange(NotificationFormType.scheduledDateTime, [hour, minute]);
              }}
            />
          </Row>
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
          <Switch
            checked={notification.isRecurring}
            onChange={(value): void => {
              handleNotificationChange(NotificationFormType.isRecurring, value);
            }}
            onColor={StyleConstants.ORANGE_COLOR}
            onHandleColor={StyleConstants.WHITE_COLOR}
            handleDiameter={styles.switchDiameter}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow={styles.switchBoxShadow}
            activeBoxShadow={styles.switchActiveBoxShadow}
            height={styles.switchHeight}
            width={styles.switchWidth}
          />
          <span className={style(styles.modalText)}>{t('is-recurring-office-notification')}</span>
        </Row>

        {notification.isRecurring && (
          <>
            <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
              <span className={style(styles.modalText)}>{t('choose-expiration-date')}</span>
              <DatePicker
                clearIcon={null}
                calendarClassName={style(styles.calendar)}
                format="dd/MM/yyyy"
                value={notification.expirationDate}
                minDate={new Date()}
                onChange={(value): void => {
                  handleNotificationChange(NotificationFormType.expirationDate, value);
                }}
              />
            </Row>

            <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
              <RecurringDays
                selectedDays={notification.recurringDays}
                onChange={(selectedDays: unknown): void => handleNotificationChange(NotificationFormType.recurringDays, selectedDays)}
              />
            </Row>
          </>
        )}

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              onClick={(): void => {
                handleSave();
              }}
              loading={isLoading}
            >
              {t('create')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCloseModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default CreateNotificationModal;
